import React from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { HERO_IMAGE } from "@/lib/brand";
import { TID } from "@/constants/testIds/dv";

export const Hero = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative bg-[#0A0A0A] text-white overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Industrial insulation project"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="absolute inset-0 diagonal-stripes opacity-50" />
      </div>

      <div className="relative container-x pt-36 pb-24 md:pt-44 md:pb-32">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#E63946]" />
            <span className="label-eyebrow">{t("hero.eyebrow")}</span>
          </div>

          <h1
            data-testid={TID.heroTitle}
            className="font-display uppercase font-extrabold leading-[0.92] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
          >
            <span className="block text-white/90">{t("hero.title_1")}</span>
            <span className="block text-[#E63946]">{t("hero.title_2")}</span>
            <span className="block text-white">{t("hero.title_3")}</span>
          </h1>

          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              onClick={scrollTo("contact")}
              data-testid={TID.heroCtaPrimary}
              className="btn-primary"
            >
              {t("hero.cta_primary")}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#services"
              onClick={scrollTo("services")}
              data-testid={TID.heroCtaSecondary}
              className="btn-outline-dark"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-2xl">
            {[
              [t("hero.stat_1_v"), t("hero.stat_1_l")],
              [t("hero.stat_2_v"), t("hero.stat_2_l")],
              [t("hero.stat_3_v"), t("hero.stat_3_l")],
            ].map(([v, l]) => (
              <div key={l} className="bg-black px-5 py-5">
                <div className="font-display text-3xl md:text-4xl text-white">{v}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/60 mt-1">
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating badge */}
        <div className="hidden lg:flex absolute right-10 bottom-16 items-center gap-3 border border-white/15 bg-black/60 backdrop-blur px-5 py-4 rounded-sm">
          <ShieldCheck className="h-6 w-6 text-[#E63946]" />
          <div className="text-xs uppercase tracking-[0.2em] text-white/80">
            Licensed · Insured · OSHA-aware
          </div>
        </div>
      </div>

      {/* bottom red bar */}
      <div className="absolute bottom-0 inset-x-0 h-1 bg-[#E63946]" />
    </section>
  );
};

export default Hero;
